import { dist2, idiv, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { archesAt, archesWarn } from '../battle/arches.ts';
import { leapsJoined, leapsOrder, leapsVert, leapsWay } from '../battle/leaps.ts';
import { leapsAdj } from '../battle/vertebras-layout.ts';
import { denOrder, denStand } from '../battle/den.ts';
import { useSkills } from './ghost.ts';
import { boteBot } from './bote-bot.ts';

/**
 * The lab's bots for the Long Hunt's chapter 2 (docs/sendas/barbaro.md, «Las Vértebras»; docs/SENDAS.md,
 * «Calibración»), asked by ai/caza-bot.ts:
 *
 * - the arches (`arches`): who knows walks from post to post and, as the foam lifts, holds to the nearest one and lets
 *   the gust sweep the pack; it goes back for Terrón at the gap. Who doesn't walks on and fights whatever comes.
 * - the leaps (`leaps`): who knows counts the arrivals and swings along the way through (the solver, as a player who
 *   reads the arches would); who doesn't goes for the nearest mark left, then the vertebra, by the fewest gaps.
 * - the den (`den`): who knows keeps the pack off Bera and cuts every howl (a stone rolled on the howler when it lies in
 *   line, else at it), then runs for the boat; who doesn't fights what is nearest, then runs for the boat.
 *
 * It only moves the hero, taps what a player would and picks its target; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/* ---------- the arches ---------- */

const archesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.vert?.arches;
  const w = s.senda?.vert?.arches;
  if (!r || !w || w.done >= 0) return;
  const at = archesAt(r, h.x, h.y, w.far);
  if (knows && archesWarn(s, r, w)) {
    // Hold to the nearest post (and hit what comes at it from there).
    let best = r.posts[0] as Pt;
    let bd = 0x7fffffff;
    for (const p of r.posts) {
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    if (!withinRange(best.x, best.y, h.x, h.y, r.post - 250)) {
      h.target = 0;
      h.forced = false;
      orderMove(s, h, best.x, best.y);
      return;
    }
    h.path = [];
    h.moving = false;
    h.destX = h.x;
    h.destY = h.y;
    const t = nearestTo(enemiesNear(s, h.x, h.y, 1100, (e) => targetable(s, e)), h.x, h.y);
    h.target = t ? t.id : 0;
    return;
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, knows ? 1800 : 2400, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
  // Terrón balking at the gap: back to him and stand by.
  const tn = s.senda?.npcs.find((n) => n.id === 'terron');
  if (w.balk === 1 && tn) {
    if (withinRange(tn.x, tn.y, h.x, h.y, r.nudgeReach - 300)) {
      h.path = [];
      h.moving = false;
      return;
    }
    return go(s, h, tn.x, tn.y);
  }
  // On along the way, a few points ahead; the knowing one not too far past a post.
  const p = r.path[imin(r.path.length - 1, at + 4)] as Pt;
  go(s, h, p.x, p.y, 200);
};

/* ---------- the leaps ---------- */

const leapsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.vert?.leaps;
  const w = s.senda?.vert?.leaps;
  if (!r || !w || w.done >= 0 || w.stuck >= 0 || w.to >= 0 || h.stun > 0) return;
  let next = -1;
  if (knows) {
    const way = leapsWay(r, w);
    next = way?.[1] ?? -1;
  } else {
    // The nearest mark left by the fewest gaps over what stands (no count kept of arrivals), then the vertebra.
    const adj = leapsAdj(r.arches.length, r.edges);
    const prev = r.arches.map(() => -2);
    prev[w.on] = -1;
    const q = [w.on];
    const goals = w.marks.filter((_, i) => !w.touched[i]);
    const want = goals.length ? goals : [leapsVert(r)];
    let hit = -1;
    for (let k = 0; k < q.length && hit < 0; k++) {
      const a = q[k] as number;
      for (const b of adj[a] as number[]) {
        if (prev[b] !== -2 || w.fallen[b]) continue;
        prev[b] = a;
        if (want.includes(b)) {
          hit = b;
          break;
        }
        q.push(b);
      }
    }
    if (hit >= 0) {
      let x = hit;
      while (prev[x] !== w.on && (prev[x] as number) >= 0) x = prev[x] as number;
      next = x;
    }
  }
  // As a tap on the arch would (the HUD's door, battle/senda.ts sendaMark).
  if (next >= 0 && leapsJoined(r, w.on, next)) leapsOrder(s, h, next);
};

/* ---------- the den ---------- */

/** The howl: a loose stone with the howler in its roll, else at it. True if busy with it. */
const cutHowl = (s: BattleState, h: Hero): boolean => {
  const r = s.rules?.senda?.vert?.den;
  const w = s.senda?.vert?.den;
  if (!r || !w) return false;
  const hw = w.howlers.find((x) => x.phase === 1 || x.phase === 2);
  if (!hw) return false;
  const e = findEnemy(s, hw.id);
  if (!e || e.hp <= 0) return false;
  if (w.job > 0) return true;
  for (let k = 0; k < r.stones.length; k++) {
    const st = w.stones[k];
    if (!st || st.mode !== 0) continue;
    const q = denStand(r, k);
    if (!withinRange(q.x, q.y, h.x, h.y, 2200)) continue;
    // The howler within the stone's roll: ahead of it along its way, and near its line.
    const p = r.stones[k] as Pt;
    const d = r.rolls[k] as Pt;
    const ax = e.x - p.x;
    const ay = e.y - p.y;
    const along = idiv(ax * d.x + ay * d.y, 1000);
    const off = Math.abs(idiv(ax * d.y - ay * d.x, 1000));
    if (along < 300 || along > r.stone.roll - 800 || off > r.stone.width - 200) continue;
    denOrder(s, h, k + 1);
    return true;
  }
  strike(s, h, e);
  return true;
};

const denBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.vert?.den;
  const w = s.senda?.vert?.den;
  if (!r || !w || w.done >= 0) return;
  if (w.launched >= 0) {
    h.target = 0;
    h.forced = false;
    return go(s, h, r.boat.x, r.boat.y);
  }
  if (knows && cutHowl(s, h)) return;
  if (w.job > 0) return;
  const b = s.senda?.npcs.find((n) => n.id === 'bera');
  if (knows && b) {
    // What is at Bera first, then what comes near; standing between her and the cave.
    const at = nearestTo(enemiesNear(s, b.x, b.y, 2600, (e) => targetable(s, e)), b.x, b.y);
    if (at) return strike(s, h, at);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 2200, (e) => targetable(s, e) && !w.howlers.some((x) => x.id === e.id)), h.x, h.y);
    if (t) return strike(s, h, t);
    // A lull: wear a howler down (the nearest), while the pack is out of the cave in ones and twos.
    const quiet = enemiesNear(s, b.x, b.y, 16000, (e) => targetable(s, e) && !w.howlers.some((x) => x.id === e.id)).length === 0;
    const hw = w.howlers.map((x) => findEnemy(s, x.id)).filter((e): e is Enemy => !!e && e.hp > 0);
    const prey = nearestTo(hw, h.x, h.y);
    if (quiet && prey && h.hp * 2 > h.stats.maxHp) return strike(s, h, prey);
    h.target = 0;
    // Halfway between Bera and the cave: near enough to either.
    return go(s, h, b.x + idiv(r.mouth.x - b.x, 3), b.y + idiv(r.mouth.y - b.y, 3), 600);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/** The chapter's act this bot plays (false: not one of its). */
export const vertBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.vert;
  if (r?.arches) archesBot(s, h, knows);
  else if (r?.leaps) leapsBot(s, h, knows);
  else if (r?.den) denBot(s, h, knows);
  // Chapter 3, «El bote» (ai/bote-bot.ts).
  else return boteBot(s, h, knows);
  return true;
};
